import React from 'react';
import styles from './Kyc.module.css';

/**
 * A plain drawing of the card the customer should photograph, with the
 * number where it sits on the real card — so they know which card and which
 * side. Not the real design (no emblems), just its layout.
 */
const Camera: React.FC = () => (
  <g transform="translate(180 92)">
    <circle r="15" fill="var(--color-primary)" />
    <path d="M-7-3h3.5l2-3h3l2 3H7v9h-14z" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round" />
    <circle cy="1.5" r="2.6" fill="none" stroke="#fff" strokeWidth="1.7" />
  </g>
);

const Photo: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g>
    <rect x={x} y={y} width="34" height="42" rx="4" fill="#e6e9ee" />
    <circle cx={x + 17} cy={y + 15} r="7" fill="#c9cfd8" />
    <path d={`M${x + 5} ${y + 40}c2-10 22-10 24 0`} fill="#c9cfd8" />
  </g>
);

const Line: React.FC<{ x: number; y: number; w: number }> = ({ x, y, w }) => (
  <rect x={x} y={y} width={w} height="5" rx="2.5" fill="#e3e6eb" />
);

const Num: React.FC<{ x: number; y: number; text: string; size?: number }> = ({ x, y, text, size = 11 }) => (
  <text x={x} y={y} fontSize={size} fontWeight="700" fill="#1a1d24" fontFamily="DM Sans, sans-serif" letterSpacing="0.5">
    {text}
  </text>
);

export const SampleCard: React.FC<{ kind: 'iqama' | 'aadhaar' | 'other'; front: boolean }> = ({ kind, front }) => (
  <svg className={styles.cardArt} viewBox="0 0 200 116" role="img" aria-label={`Sample ${kind} ${front ? 'front' : 'back'}`}>
    <rect x="1" y="1" width="198" height="114" rx="10" fill="#fff" stroke="#dfe3e8" strokeWidth="1.5" />
    {kind === 'other' ? (
      front ? (
        <>
          <rect x="1" y="1" width="198" height="20" rx="10" fill="#1e3a8a" />
          <rect x="1" y="12" width="198" height="9" fill="#1e3a8a" />
          <text x="12" y="15" fontSize="8.5" fontWeight="700" fill="#fff" fontFamily="DM Sans, sans-serif" letterSpacing="0.6">PASSPORT · NATIONAL ID</text>
          <Photo x={12} y={30} />
          <Line x={56} y={33} w={80} />
          <Line x={56} y={45} w={60} />
          <Line x={56} y={57} w={70} />
          <text x="12" y="86" fontSize="7" fill="#8a909c" fontFamily="DM Sans, sans-serif">ID number</text>
          <Num x={12} y={100} text="X0000000" />
        </>
      ) : (
        <>
          <Line x={12} y={16} w={120} />
          <Line x={12} y={28} w={96} />
          <Line x={12} y={40} w={108} />
          <Line x={12} y={52} w={70} />
        </>
      )
    ) : kind === 'iqama' ? (
      front ? (
        <>
          <rect x="1" y="1" width="198" height="20" rx="10" fill="#0f6b4f" />
          <rect x="1" y="12" width="198" height="9" fill="#0f6b4f" />
          <text x="12" y="15" fontSize="8.5" fontWeight="700" fill="#fff" fontFamily="DM Sans, sans-serif" letterSpacing="0.6">RESIDENT IDENTITY · IQAMA</text>
          <Photo x={12} y={30} />
          <Line x={56} y={33} w={80} />
          <Line x={56} y={45} w={60} />
          <Line x={56} y={57} w={70} />
          <text x="12" y="86" fontSize="7" fill="#8a909c" fontFamily="DM Sans, sans-serif">ID number</text>
          <Num x={12} y={100} text="2XXX XXX XXX" />
        </>
      ) : (
        <>
          <Line x={12} y={14} w={120} />
          <Line x={12} y={26} w={90} />
          <Line x={12} y={38} w={105} />
          <g fill="#2b2f36">
            {[0, 3, 5, 9, 11, 14, 18, 20, 23, 27, 29, 33, 36, 38, 42, 45, 49, 51, 54, 58, 61, 63, 67, 70, 74, 77, 80, 84, 86, 90, 93, 96].map((x) => (
              <rect key={x} x={14 + x * 1.15} y="58" width={x % 3 ? 1.6 : 2.6} height="26" />
            ))}
          </g>
        </>
      )
    ) : front ? (
      <>
        <rect x="1" y="1" width="198" height="7" rx="3" fill="#f59e0b" />
        <rect x="1" y="108" width="198" height="7" rx="3" fill="#16a34a" />
        <text x="12" y="22" fontSize="8.5" fontWeight="700" fill="#1a1d24" fontFamily="DM Sans, sans-serif" letterSpacing="0.6">AADHAAR · GOVERNMENT OF INDIA</text>
        <Photo x={12} y={30} />
        <Line x={56} y={33} w={78} />
        <Line x={56} y={45} w={52} />
        <Line x={56} y={57} w={64} />
        <Num x={42} y={96} text="XXXX XXXX XXXX" size={12} />
      </>
    ) : (
      <>
        <rect x="1" y="1" width="198" height="7" rx="3" fill="#f59e0b" />
        <text x="12" y="22" fontSize="7" fill="#8a909c" fontFamily="DM Sans, sans-serif">Address · PIN</text>
        <Line x={12} y={30} w={110} />
        <Line x={12} y={42} w={96} />
        <Line x={12} y={54} w={70} />
        <Num x={12} y={74} text="PIN 6XXXXX" size={9} />
        <rect x="130" y="24" width="34" height="34" fill="none" stroke="#2b2f36" strokeWidth="1.2" />
        <rect x="134" y="28" width="9" height="9" fill="#2b2f36" />
        <rect x="151" y="28" width="9" height="9" fill="#2b2f36" />
        <rect x="134" y="45" width="9" height="9" fill="#2b2f36" />
        <rect x="149" y="45" width="5" height="5" fill="#2b2f36" />
        <Num x={42} y={100} text="XXXX XXXX XXXX" size={10} />
      </>
    )}
    <Camera />
  </svg>
);
